import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle,
  Calendar,
  LogOut,
  User,
  BadgeCheck,
  XCircle,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { isFuture, isPast, isToday } from "date-fns";
import { compressImage } from "@/lib/imageCompression";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

// ==================== Utils ====================
function toLocalISOString(date: Date) {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString();
}

// Detecta HEIC/HEIF mesmo quando o navegador não preenche file.type
const isHeicLike = (f: File) => {
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return (
    f.type === "image/heic" ||
    f.type === "image/heif" ||
    ext === "heic" ||
    ext === "heif"
  );
};
const isImageOrHeic = (f: File) =>
  (f.type?.startsWith("image/") ?? false) || isHeicLike(f);

// Converte HEIC/HEIF para JPEG usando heic2any (import dinâmico)
async function convertHeicToJpeg(file: File): Promise<File> {
  const heic2any = (await import("heic2any")).default;
  const blob = (await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.92,
  })) as Blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  return new File([blob], newName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

// ==================== Tipos ====================
type Desafio = { id: string; name: string; cor: string };
type CheckinsHojeMap = Record<string, string[]>;

// ==================== Componente ====================
export default function CheckIn() {
  const [challenge, setChallenge] = useState("");
  const [userChallenges, setUserChallenges] = useState<Desafio[]>([]);
  const [checkinsHoje, setCheckinsHoje] = useState<string[]>([]);
  const [fotosHojePorDesafio, setFotosHojePorDesafio] =
    useState<CheckinsHojeMap>({});

  const [fotosSelecionadas, setFotosSelecionadas] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isUploadingFoto, setIsUploadingFoto] = useState(false);

  const { toast } = useToast();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const today = new Date();
  const dataFormatada = today.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });

  // cleanup previews
  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p));
    };
  }, [previews]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!user?.id) return;
      setIsPageLoading(true);
      try {
        await Promise.all([
          buscarDesafiosInscritos(user.id),
          buscarCheckinsHoje(user.id),
        ]);
      } finally {
        setIsPageLoading(false);
      }
    };
    bootstrap();
  }, [user?.id]);

  const buscarDesafiosInscritos = async (usuarioId: string) => {
    try {
      const todayStr = (() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const d = String(now.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      })();

      const { data, error } = await supabase
        .from("usuarios_desafios")
        .select(
          `
          desafio_id,
          desafios ( id, nome, nome_fantasia, cor, data_inicio, data_fim )
        `
        )
        .eq("usuario_id", usuarioId)
        .lte("desafios.data_inicio", todayStr)
        .gte("desafios.data_fim", todayStr);

      if (error) {
        console.error("Erro ao buscar inscrições:", error);
        toast({
          title: "Erro ao carregar desafios",
          description: "Tente novamente mais tarde.",
          variant: "destructive",
        });
        return;
      }

      const desafiosUsuario: Desafio[] = (data ?? [])
        .filter((it: any) => it?.desafios)
        .map((it: any) => ({
          id: String(it.desafios.id),
          name: it.desafios.nome_fantasia ?? it.desafios.nome,
          cor: it.desafios.cor,
        }));

      setUserChallenges(desafiosUsuario);
    } catch (err) {
      console.error("Erro ao buscar desafios:", err);
    }
  };

  const buscarCheckinsHoje = async (usuarioId: string) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from("checkins")
      .select("desafio_id, foto_url")
      .eq("usuario_id", usuarioId)
      .gte("created_at", toLocalISOString(d))
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Erro ao buscar check-ins de hoje:", error);
      return;
    }

    const ids = new Set<string>();
    const fotosMap: CheckinsHojeMap = {};

    (data ?? []).forEach((row: any) => {
      const id = String(row.desafio_id);
      ids.add(id);
      const fotos: string[] = Array.isArray(row.foto_url) ? row.foto_url : [];
      fotosMap[id] = (fotosMap[id] ?? []).concat(fotos);
    });

    setCheckinsHoje(Array.from(ids));
    setFotosHojePorDesafio(fotosMap);
  };

  const isDateDisabled = (date: Date) =>
    isFuture(date) || (isPast(date) && !isToday(date));

  const MAX_FOTOS = 3;
  const MAX_MB = 20;

  // ==================== Upload/Conversão ====================
  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const totalPretendido = fotosSelecionadas.length + files.length;
    if (totalPretendido > MAX_FOTOS) {
      toast({
        title: "Limite de fotos",
        description: `Máximo de ${MAX_FOTOS} fotos por check-in.`,
        variant: "destructive",
      });
      return;
    }

    // Validação (aceita HEIC/HEIF mesmo quando type vazio)
    for (const f of files) {
      if (!isImageOrHeic(f)) {
        toast({
          title: "Formato não suportado",
          description: `“${f.name}” não foi reconhecido como imagem. Aceitamos JPG, PNG, WEBP e HEIC/HEIF.`,
          variant: "destructive",
        });
        return;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast({
          title: "Arquivo muito grande",
          description: `“${f.name}” excede ${MAX_MB}MB.`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setIsUploadingFoto(true);

      const prepared = await Promise.all(
        files.map(async (f) => {
          // 1) Converte HEIC/HEIF para JPEG
          const maybeJpeg = isHeicLike(f) ? await convertHeicToJpeg(f) : f;

          // 2) Comprime (sua função já exporta JPEG)
          const out = await compressImage(maybeJpeg);
          return new File(
            [out],
            out.name ?? maybeJpeg.name.replace(/\.(png|webp)$/i, ".jpg"),
            {
              type: "image/jpeg",
            }
          );
        })
      );

      setFotosSelecionadas((prev) => [...prev, ...prepared]);
      const newPreviews = prepared.map((c) => URL.createObjectURL(c));
      setPreviews((prev) => [...prev, ...newPreviews]);

      toast({
        title: "Fotos adicionadas",
        description: `${prepared.length} imagem(ns) pronta(s) para envio.`,
      });
    } catch (err) {
      console.error("Erro ao processar imagens:", err);
      toast({
        title: "Não foi possível processar as imagens",
        description:
          "Use formatos comuns (JPG, PNG, WEBP ou HEIC/HEIF). No iPhone, você pode ativar ‘Mais Compatível’ em Ajustes > Câmera > Formatos.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingFoto(false);
    }
  };

  const removerFoto = (idx: number) => {
    setFotosSelecionadas((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => {
      const p = [...prev];
      const [removed] = p.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed);
      return p;
    });
  };

  const limparFotos = () => {
    previews.forEach((p) => URL.revokeObjectURL(p));
    setFotosSelecionadas([]);
    setPreviews([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!challenge) {
      toast({
        title: "Desafio não selecionado",
        description: "Por favor, selecione um desafio.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const d = new Date();
      d.setHours(0, 0, 0, 0);

      const { data: checkinExistente, error: checkinError } = await supabase
        .from("checkins")
        .select("id")
        .eq("usuario_id", user?.id)
        .eq("desafio_id", challenge)
        .gte("created_at", toLocalISOString(d));

      if (checkinError) throw checkinError;
      if ((checkinExistente ?? []).length > 0) {
        toast({
          title: "Check-in já realizado",
          description: "Você já fez check-in neste desafio hoje.",
          variant: "destructive",
        });
        return;
      }

      // Upload das fotos (opcional)
      let fotosPublicUrls: string[] = [];
      if (fotosSelecionadas.length > 0) {
        const basePath = `${user?.id}/${challenge}/${Date.now()}`;
        const uploads = await Promise.all(
          fotosSelecionadas.map(async (file, i) => {
            // Garante extensão e contentType consistentes
            const ext =
              file.type === "image/jpeg"
                ? "jpg"
                : file.name.split(".").pop() || "jpg";
            const path = `${basePath}_${i}.${ext}`;

            const { error } = await supabase.storage
              .from("checkins-fotos")
              .upload(path, file, {
                cacheControl: "3600",
                upsert: false,
                contentType: file.type || "image/jpeg",
              });
            if (error) throw error;

            const { data } = supabase.storage
              .from("checkins-fotos")
              .getPublicUrl(path);
            return data.publicUrl as string;
          })
        );
        fotosPublicUrls = uploads.filter(Boolean);
      }

      const { error: insertError } = await supabase.from("checkins").insert([
        {
          usuario_id: user?.id,
          desafio_id: challenge,
          foto_url: fotosPublicUrls.length ? fotosPublicUrls : null,
          created_at: toLocalISOString(new Date()),
        },
      ]);

      if (insertError) throw insertError;

      const desafioNome =
        userChallenges.find((c) => c.id === challenge)?.name || "";
      toast({
        title: "Check-in realizado!",
        description: `Parabéns! Você fez o check-in dia ${dataFormatada} do Desafio ${desafioNome}`,
      });

      setChallenge("");
      limparFotos();
      if (user?.id) await buscarCheckinsHoje(user.id);
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro no check-in",
        description: "Não foi possível realizar o check-in. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const desafiosDisponiveis = userChallenges.filter(
    (item) => checkinsHoje.indexOf(item.id) === -1
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-secondary p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img
                src="/pele-bonita.png"
                alt="Campanha Pele Bonita"
                className="mx-auto my-3 w-28 max-w-sm h-auto object-contain rounded-b-lg"
              />
            </div>

            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <span>Nome: {user?.nome}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-4 h-4 mr-1" /> Sair
              </Button>
            </div>
          </div>

          {/* Form */}
          <Card className="shadow-lg border-primary/20">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-center text-azul space-y-2">
                {isPageLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    <span className="text-sm text-muted-foreground">
                      Carregando seus desafios...
                    </span>
                  </div>
                ) : userChallenges.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2">
                    <XCircle className="h-8 w-8 text-red-500" />
                    <span>
                      Você ainda não está inscrito em nenhum desafio em
                      andamento.
                    </span>
                  </div>
                ) : desafiosDisponiveis.length > 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2">
                    <CheckCircle className="h-8 w-8 text-azul" />
                    <span>Você tem check-ins disponíveis para hoje!</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <span>Check-in feito. Parabéns!</span>
                  </div>
                )}
              </CardTitle>

              {!isPageLoading && desafiosDisponiveis.length > 0 && (
                <CardDescription>
                  <span className="mx-auto mt-2 inline-block rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 text-center whitespace-normal break-words leading-relaxed max-w-[280px] sm:max-w-none">
                    Complete seu desafio de hoje e mantenha sua jornada de
                    bem-estar!
                  </span>
                </CardDescription>
              )}
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {!isPageLoading && desafiosDisponiveis.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="challenge">Desafio do Dia</Label>
                    <select
                      id="challenge"
                      className="h-12 w-full border rounded-md px-3"
                      value={challenge}
                      onChange={(e) => setChallenge(e.target.value)}
                    >
                      <option value="" disabled>
                        Selecione o desafio
                      </option>
                      {desafiosDisponiveis.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>

                    {/* Fotos (opcional) */}
                    <div className="space-y-2">
                      <Label htmlFor="fotos-input">
                        Fotos do desafio (opcional)
                      </Label>

                      <div
                        className={[
                          "relative border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                          "hover:border-primary/50",
                          isUploadingFoto
                            ? "opacity-60 pointer-events-none"
                            : "cursor-pointer",
                        ].join(" ")}
                      >
                        <input
                          id="fotos-input"
                          type="file"
                          accept="image/*,.heic,.heif" // <- libera HEIC/HEIF
                          multiple
                          onChange={handleFotoChange}
                          disabled={isUploadingFoto}
                          aria-label="Selecionar fotos do check-in (opcional)"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />

                        <div className="space-y-2 pointer-events-none">
                          <div className="flex justify-center">
                            {isUploadingFoto ? (
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                            ) : (
                              <ImageIcon className="h-12 w-12 text-muted-foreground" />
                            )}
                          </div>
                          <p className="text-primary font-medium">
                            Clique para selecionar{" "}
                            {fotosSelecionadas.length ? "mais fotos" : "fotos"}{" "}
                            (opcional)
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Até {MAX_FOTOS} imagens • JPG, PNG, WEBP ou
                            HEIC/HEIF (máx. {MAX_MB}MB cada)
                          </p>
                        </div>
                      </div>

                      {/* Thumbs */}
                      {previews.length > 0 && (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-3">
                            {previews.map((src, idx) => (
                              <div key={src} className="relative">
                                <img
                                  src={src}
                                  alt={`Prévia ${idx + 1}`}
                                  className="h-24 w-24 object-cover rounded-md border"
                                />
                                <button
                                  type="button"
                                  onClick={() => removerFoto(idx)}
                                  className="absolute -top-2 -right-2 bg-white rounded-full border shadow p-1"
                                  title="Remover"
                                  aria-label="Remover"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={limparFotos}
                            >
                              Limpar todas
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full h-12 text-lg bg-gradient-primary hover:shadow-glow transition-all duration-300"
                      disabled={isLoading || desafiosDisponiveis.length === 0}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Realizando Check-in...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Fazer Check-in
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </form>

              {/* Cards de hoje + Fotos do dia */}
              {!isPageLoading && checkinsHoje.length > 0 && (
                <div className="flex flex-wrap justify-center gap-4 mt-6">
                  {userChallenges.map((item) => {
                    const isCheckedToday = checkinsHoje.indexOf(item.id) !== -1;
                    if (!isCheckedToday) return null;

                    const fotos = fotosHojePorDesafio[item.id] ?? [];

                    return (
                      <div
                        key={item.id}
                        style={{ backgroundColor: item?.cor || "#002366" }}
                        className="text-white rounded-xl p-4 w-full sm:w-[280px] md:w-[320px] text-center shadow-md"
                      >
                        <div className="flex justify-center mb-2">
                          <BadgeCheck
                            className="w-10 h-10"
                            style={{ color: "#002366" }}
                          />
                        </div>
                        <div
                          className="text-2xl font-bold"
                          style={{ color: "#002366" }}
                        >
                          {dataFormatada}
                        </div>
                        <hr className="my-2 border-white/40 bg-primary" />
                        <div
                          className="text-lg font-semibold leading-tight"
                          style={{ color: "#002366" }}
                        >
                          Desafio <br /> {item.name}
                        </div>

                        {fotos.length > 0 && (
                          <div className="mt-3">
                            <div
                              className="text-sm font-medium mb-2"
                              style={{ color: "#002366" }}
                            >
                              Fotos de hoje ({fotos.length})
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              {fotos.slice(0, 6).map((src, i) => (
                                <a
                                  key={i}
                                  href={src}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Abrir em nova aba"
                                  className="block rounded overflow-hidden bg-white"
                                >
                                  <img
                                    src={src}
                                    alt={`Foto ${i + 1}`}
                                    className="h-20 w-full object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-8 flex justify-center">
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/meus-checkins")}
              className="max-w-xs"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Ver Meus Check-ins
            </Button>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
